import type { ReactNode } from 'react';
import React, { memo, useMemo } from 'react';

import { Badge, Card, Space, Typography } from '@sravni/react-design-system';

import { formatMoney } from '@src/helpers/formatMoney';

import { DAYS_IN_WEEK } from '../../config';
import { formatDateWithOffset, formatNextPaymentDate } from '../../lib';
import { FullDealEntryPointFormCalculations } from '../FullDealEntryPointFormCalculations/FullDealEntryPointFormCalculations';
import {
  LONG_AMOUNT_COEFFICENT_CHANGE,
  LONG_TERM_SECOND_RANGE_COEFFICIENT,
  LONG_TERM_FIRST_RANGE_COEFFICIENT,
} from '../FullDealEntryPointFormCalculations/FullDealEntryPointFormCalculations.config';
import { calculateShortDebt, calculateLongDebt } from '../FullDealEntryPointFormCalculations/lib';

import styles from './InlineCalculations.module.scss';

interface Props {
  term: number;
  amount: number;
  children: ReactNode;
  isMobile: boolean;
}

export const InlineCalculations = memo(({ term, amount, children, isMobile }: Props) => {
  const withoutOverpayment = term < 22;
  const isShowShortDebt = term < 34;

  const amountWithOverpayment = calculateShortDebt(amount, term);
  const repaymentDate = formatDateWithOffset(term - 1, { prefix: 'Вернуть до' });

  const langRepaymentDate = formatNextPaymentDate({ prefix: 'раз в 2 недели с' });

  const termByWeeks = Math.round(term / DAYS_IN_WEEK);
  const calculations = calculateLongDebt(
    amount,
    termByWeeks,
    amount > LONG_AMOUNT_COEFFICENT_CHANGE ? LONG_TERM_SECOND_RANGE_COEFFICIENT : LONG_TERM_FIRST_RANGE_COEFFICIENT,
  );

  // @todo: Пока не удаляем, скорее всего потом пригодится
  // termByWeeks > TERM_SLIDER_MIN_THIRD_RANGE && termByWeeks < TERM_SLIDER_MAX_THIRD_RANGE
  //   ? calculateLongDebt(amount, termByWeeks, LONG_TERM_FIRST_RANGE_COEFFICIENT)
  //   : calculateLongDebt(amount, termByWeeks, LONG_TERM_SECOND_RANGE_COEFFICIENT);

  const amountValue = useMemo(() => {
    if (isShowShortDebt) {
      return withoutOverpayment ? formatMoney(amount) : formatMoney(amountWithOverpayment);
    }
    return formatMoney(calculations);
  }, [amount, amountWithOverpayment, calculations, isShowShortDebt, withoutOverpayment]);

  const overpayment = useMemo(() => formatMoney(amountWithOverpayment, { withSymbol: false }), [amountWithOverpayment]);

  const Content = (
    <div className={styles.content}>
      <Space className={styles.termList}>
        <Typography.Text className={styles.contentText}>{repaymentDate}</Typography.Text>
      </Space>
      <Space direction="row" align="center" size={8} className={styles.amountList}>
        <Typography.Text className={styles.headingAmount}>{amountValue}</Typography.Text>
        {withoutOverpayment && (
          <>
            <Typography.Text className={styles.contentAmount}>{overpayment}</Typography.Text>
            <Badge className={styles.badge} color="red" variant="primary" text="Бесплатно" />
          </>
        )}
        {!isShowShortDebt && <Typography.Text className={styles.contentText}>{langRepaymentDate}</Typography.Text>}
      </Space>
    </div>
  );

  if (isMobile) {
    return (
      <Space direction="column" size={16}>
        {Content}
        <div className={styles.children}>{children}</div>
      </Space>
    );
  }

  return (
    <Card className={styles.container} color="dark">
      <Space direction="row" align="center" size={12}>
        {Content}
        <div className={styles.children}>{children}</div>
      </Space>
    </Card>
  );
});

FullDealEntryPointFormCalculations.displayName = 'FullDealEntryPointFormCalculations';
